import { useEffect, useRef } from 'react';
import { WishlistIcon } from '@/shared/icons/icons';
import { cn } from '@/shared/utils/cn';

const FILL_DURATION_MS = 660;

/** Briefly fills the heart when the synchronized wishlist count increases. */
export function WishlistFeedbackIcon({ count, enabled, className }: {
  count: number;
  enabled: boolean;
  className?: string;
}) {
  const rootRef = useRef<HTMLSpanElement>(null);
  const heartRef = useRef<HTMLSpanElement>(null);
  const burstRef = useRef<SVGSVGElement>(null);
  const fillRef = useRef<HTMLSpanElement>(null);
  const previous = useRef({ count, enabled });

  useEffect(() => {
    const shouldAnimate = enabled && previous.current.enabled && count > previous.current.count;
    previous.current = { count, enabled };
    const element = fillRef.current;
    
    if (!enabled || count === 0) {
      rootRef.current?.getAnimations({ subtree: true }).forEach(animation => animation.cancel());
      return;
    }
    
    if (!element || !shouldAnimate || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    
    rootRef.current?.getAnimations({ subtree: true }).forEach(animation => animation.cancel());
    
    const fillAnim = element.animate([
      { clipPath: 'inset(100% 0 0 0)' },
      { clipPath: 'inset(0% 0 0 0)' },
    ], { duration: FILL_DURATION_MS, easing: 'ease-in-out', fill: 'forwards' });

    // The reference pops only once the rising fill reaches the top.
    heartRef.current?.animate([
      { transform: 'scale(1)' },
      { transform: 'scale(1.22)', offset: 0.3 },
      { transform: 'scale(0.97)', offset: 0.75 },
      { transform: 'scale(1)' },
    ], { delay: FILL_DURATION_MS, duration: 460, easing: 'ease-out' });

    const burstAnim = burstRef.current?.animate([
      { opacity: 0, transform: 'translate(1px, 1px) scale(0.7)' },
      { opacity: 1, transform: 'translate(0, 0) scale(1)', offset: 0.15 },
      { opacity: 1, transform: 'translate(-1px, -1px) scale(1)', offset: 0.55 },
      { opacity: 0, transform: 'translate(-2px, -2px) scale(1)' },
    ], { delay: FILL_DURATION_MS, duration: 600, easing: 'ease-out' });

    // Reset back to white once the final animation finishes
    if (burstAnim) {
      burstAnim.onfinish = () => {
        fillAnim.cancel();
        if (fillRef.current) {
          fillRef.current.style.clipPath = 'inset(100% 0 0 0)';
        }
      };
    }
  }, [count, enabled]);

  useEffect(() => {
    const element = rootRef.current;
    return () => element?.getAnimations({ subtree: true }).forEach(animation => animation.cancel());
  }, []);

  return (
    <span ref={rootRef} className={cn('relative inline-flex h-6 w-6', className)} aria-hidden="true">
      <span ref={heartRef} className="relative inline-flex h-full w-full">
        <WishlistIcon className="h-full w-full" />
        <span ref={fillRef} className="pointer-events-none absolute inset-0 text-[#B98599]" style={{ clipPath: 'inset(100% 0 0 0)' }}>
          <WishlistIcon filled className="h-full w-full" />
        </span>
      </span>
      <svg ref={burstRef} className="pointer-events-none absolute -left-2 -top-2 h-3 w-3 text-[#B98599] opacity-0" viewBox="0 0 12 12" fill="none" aria-hidden="true">
        <path d="M8.5 5.5 7 1.5M5.5 7 3 3.5M4 9 1 7" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
      </svg>
    </span>
  );
}